"use client";

import { usePathname } from "next/navigation";
import { Button } from "@/components/ui/button";
import WarningSolid from "@repo/icons/react/WarningSolid";
import { useCurrentUser } from "@/hooks/use-current-user";
import { openPosthogSupport } from "@/lib/posthog-support";
import {
  createContext,
  useContext,
  useState,
  type PropsWithChildren,
} from "react";

type ProductFeedbackContextValue = {
  openReporter: () => void;
};

const productFeedbackContext = createContext<ProductFeedbackContextValue>({
  openReporter: () => undefined,
});

export function useProductFeedback() {
  return useContext(productFeedbackContext);
}

export function ProductFeedbackProvider({ children }: PropsWithChildren) {
  const { user, loaded } = useCurrentUser();
  const pathname = usePathname();
  const isOsRoute = pathname === "/os" || pathname?.startsWith("/os/");
  const [opening, setOpening] = useState(false);

  async function openReporter() {
    if (opening) return;
    setOpening(true);
    try {
      await openPosthogSupport();
    } finally {
      setOpening(false);
    }
  }

  return (
    <productFeedbackContext.Provider value={{ openReporter }}>
      {children}
      {loaded && user && isOsRoute ? (
        <Button
          type="button"
          onClick={() => void openReporter()}
          disabled={opening}
          className="fixed bottom-5 right-5 z-40 inline-flex h-11 gap-2 rounded-full px-4 shadow-lg"
          aria-label="Report an issue"
        >
          <WarningSolid className="h-4 w-4" />
          Report issue
        </Button>
      ) : null}
    </productFeedbackContext.Provider>
  );
}
